/**
 * ParticlePools — the four unified particle pools from masterplan §6.4.
 *
 *   gold    — warm yellow, tiny, falls or drifts
 *              used for: score ticks, victory rain, powerup pickup milestones
 *   sapphire — cyan/blue angular shards
 *              used for: button clicks, UI interactions, info modal pop-in
 *   coral   — red/orange embers
 *              used for: boss attacks, damage, danger-state emphasis
 *   pearl   — white radial bloom
 *              used for: star rewards, achievement toasters, milestone bursts
 *
 * Every click/hit/milestone in the game should emit from ONE of these pools
 * so visual noise stays unified. Additional bespoke particles (the toxic
 * drip in BossScene, bubble pops in GameScene) coexist — ParticlePools is a
 * complement, not a replacement.
 *
 * Usage:
 *   import { ParticlePools } from '@/utils/ParticlePools';
 *   ParticlePools.get(scene).burst('gold', x, y, 20);
 *   ParticlePools.get(scene).drift('pearl', x, y, 8);
 */

import Phaser from 'phaser';

export type PoolName = 'gold' | 'sapphire' | 'coral' | 'pearl';

interface PoolConfig {
  tints: number[];
  // Default burst characteristics — individual call can override
  burst: {
    speed: { min: number; max: number };
    scale: { start: number; end: number };
    alpha: { start: number; end: number };
    lifespan: { min: number; max: number };
    gravityY: number;
    blend: Phaser.BlendModes;
  };
  // Default drift (ambient rain) characteristics
  drift: {
    speed: { min: number; max: number };
    angle: { min: number; max: number };
    scale: { start: number; end: number };
    alpha: { start: number; end: number };
    lifespan: { min: number; max: number };
    gravityY: number;
    blend: Phaser.BlendModes;
  };
}

const POOL_CONFIGS: Record<PoolName, PoolConfig> = {
  gold: {
    // Warm yellow — goldLight #FFEBA6 + gold #E7C26B + goldGlow #FFF4C2
    tints: [0xFFEBA6, 0xE7C26B, 0xFFF4C2, 0xFFD700],
    burst: {
      speed: { min: 160, max: 360 },
      scale: { start: 1.4, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 500, max: 900 },
      gravityY: 220,
      blend: Phaser.BlendModes.ADD,
    },
    drift: {
      speed: { min: 30, max: 90 },
      angle: { min: 70, max: 110 },
      scale: { start: 0.9, end: 0 },
      alpha: { start: 0.8, end: 0 },
      lifespan: { min: 2000, max: 3500 },
      gravityY: 40,
      blend: Phaser.BlendModes.ADD,
    },
  },
  sapphire: {
    // Cyan / deep blue — sapphireLight #5A8EEA + aqua #4BBFD4 + aquaLight #9EE8F4
    tints: [0x5A8EEA, 0x4BBFD4, 0x9EE8F4, 0x1E54B0],
    burst: {
      speed: { min: 180, max: 400 },
      scale: { start: 1.1, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 400, max: 700 },
      gravityY: 60,
      blend: Phaser.BlendModes.ADD,
    },
    drift: {
      speed: { min: 15, max: 60 },
      angle: { min: 250, max: 290 },
      scale: { start: 0.8, end: 0 },
      alpha: { start: 0.7, end: 0 },
      lifespan: { min: 1500, max: 2800 },
      gravityY: -20, // rise upward like bubbles
      blend: Phaser.BlendModes.ADD,
    },
  },
  coral: {
    // Red / orange embers — rubyCoral #D94F3A + rubyCoralLight #F59E6C + warningAmber #F5A623
    tints: [0xD94F3A, 0xF59E6C, 0xF5A623, 0x8B2A20],
    burst: {
      speed: { min: 140, max: 340 },
      scale: { start: 1.3, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 450, max: 850 },
      gravityY: 180,
      blend: Phaser.BlendModes.ADD,
    },
    drift: {
      speed: { min: 20, max: 80 },
      angle: { min: 250, max: 290 }, // rising like embers
      scale: { start: 1.0, end: 0 },
      alpha: { start: 0.9, end: 0 },
      lifespan: { min: 1800, max: 3200 },
      gravityY: -60,
      blend: Phaser.BlendModes.ADD,
    },
  },
  pearl: {
    // White radial bloom — pearlWhite #FFF8E8 + pure white
    tints: [0xFFF8E8, 0xFFFFFF, 0xFFF4C2],
    burst: {
      speed: { min: 100, max: 280 },
      scale: { start: 1.8, end: 0 },
      alpha: { start: 1, end: 0 },
      lifespan: { min: 600, max: 1100 },
      gravityY: 0, // radial, no gravity
      blend: Phaser.BlendModes.ADD,
    },
    drift: {
      speed: { min: 10, max: 50 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.7, end: 0 },
      alpha: { start: 0.6, end: 0 },
      lifespan: { min: 2500, max: 4000 },
      gravityY: 0,
      blend: Phaser.BlendModes.ADD,
    },
  },
};

export class ParticlePools {
  private static instances = new WeakMap<Phaser.Scene, ParticlePools>();

  static get(scene: Phaser.Scene): ParticlePools {
    let inst = this.instances.get(scene);
    if (!inst) {
      inst = new ParticlePools(scene);
      this.instances.set(scene, inst);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.instances.delete(scene);
      });
    }
    return inst;
  }

  private scene: Phaser.Scene;
  /** Two emitters per pool — one tuned for burst, one for drift. */
  private burstEmitters: Partial<Record<PoolName, Phaser.GameObjects.Particles.ParticleEmitter>> = {};
  private driftEmitters: Partial<Record<PoolName, Phaser.GameObjects.Particles.ParticleEmitter>> = {};

  private constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.ensureTexture();
  }

  /** Make sure the shared pixel texture exists. */
  private ensureTexture(): void {
    if (this.scene.textures.exists('particle_pool_pixel')) return;
    const g = this.scene.add.graphics();
    g.fillStyle(0xFFFFFF, 1);
    g.fillCircle(6, 6, 6);
    g.generateTexture('particle_pool_pixel', 12, 12);
    g.destroy();
  }

  /** Lazy-create a burst emitter for the given pool. */
  private ensureBurst(name: PoolName): Phaser.GameObjects.Particles.ParticleEmitter {
    let emitter = this.burstEmitters[name];
    if (emitter) return emitter;
    const config = POOL_CONFIGS[name];
    emitter = this.scene.add.particles(0, 0, 'particle_pool_pixel', {
      speed: config.burst.speed,
      angle: { min: 0, max: 360 },
      scale: config.burst.scale,
      alpha: config.burst.alpha,
      lifespan: config.burst.lifespan,
      gravityY: config.burst.gravityY,
      tint: config.tints,
      blendMode: config.burst.blend,
      frequency: -1, // manual emission only
    }).setDepth(1500);
    this.burstEmitters[name] = emitter;
    return emitter;
  }

  /** Lazy-create a drift emitter for the given pool. */
  private ensureDrift(name: PoolName): Phaser.GameObjects.Particles.ParticleEmitter {
    let emitter = this.driftEmitters[name];
    if (emitter) return emitter;
    const config = POOL_CONFIGS[name];
    emitter = this.scene.add.particles(0, 0, 'particle_pool_pixel', {
      speed: config.drift.speed,
      angle: config.drift.angle,
      scale: config.drift.scale,
      alpha: config.drift.alpha,
      lifespan: config.drift.lifespan,
      gravityY: config.drift.gravityY,
      tint: config.tints,
      blendMode: config.drift.blend,
      frequency: -1,
    }).setDepth(1500);
    this.driftEmitters[name] = emitter;
    return emitter;
  }

  /**
   * Emit a one-shot burst of N particles at (x, y). Default 12 particles.
   */
  burst(name: PoolName, x: number, y: number, count: number = 12): void {
    this.ensureBurst(name).emitParticleAt(x, y, count);
  }

  /**
   * Emit a smaller ambient drift at (x, y). Default 5 particles at a gentler tempo.
   */
  drift(name: PoolName, x: number, y: number, count: number = 5): void {
    this.ensureDrift(name).emitParticleAt(x, y, count);
  }

  /**
   * Emit a bigger "milestone" burst — the one you save for level-ups and
   * score thresholds. 30 particles.
   */
  milestone(name: PoolName, x: number, y: number): void {
    this.burst(name, x, y, 30);
  }
}
